import { parseDate } from "@/utilities/datetime";
import type { PickemsMarket } from "../models/pickems";
import type { Always } from "@/types/utilities";
import { getEntryStatus } from "@/hooks/firestore/betting/useEntries";
import type { PickemsEntry } from "../models/entries";

export const getPickemsMarketBetStatus = (
  marketStatus: PickemsMarket["status"],
) => {
  if (typeof marketStatus !== "string" || !marketStatus) {
    return;
  }

  switch (marketStatus) {
    case "DEACTIVATED":
      return "LIVE";
    case "SETTLED":
    case "CANCELLED":
      return "RESULTED";
    case "ACTIVE":
      // this will be shown if a market has passed its scheduled start time but has not been deactivated yet
      return "WAITING";
    default:
      return marketStatus;
  }
};

export const transformPickemsMarketEvent = (
  event: PickemsMarket["event"],
  market: PickemsMarket,
) => {
  // Build a record of competitors to store at the top-level of the event object
  const competitors = Object.values(market.outcomes || {}).reduce<
    Record<string, Always<PickemsMarket["outcomes"][number]["competitor"]>>
  >((acc, outcome) => {
    if (outcome.competitorId && outcome.competitor) {
      acc[outcome.competitorId] = outcome.competitor;
    }
    return acc;
  }, {});

  const out = {
    ...event,
    competitors,
    scheduledStartTime: parseDate(event.scheduledStartTime),
  };

  return out;
};

export const transformPickemsMarkets = (markets: PickemsMarket[]) => {
  const out = [];

  for (const market of markets) {
    const { event, scheduledStartTime, outcomes, ...restMarket } = market;

    // Add a new odds property to each outcome, derived from the pick's points
    const newOutcomeEntries = Object.entries(outcomes).map(
      ([outcomeId, outcome]) => {
        return [
          outcomeId,
          {
            ...outcome,
            odds: outcome.points,
          },
        ];
      },
    );

    out.push({
      ...restMarket,
      event: transformPickemsMarketEvent(event, market),
      betStatus: getPickemsMarketBetStatus(restMarket.status),
      outcomes: Object.fromEntries(newOutcomeEntries),
      scheduledStartTime: parseDate(event.scheduledStartTime),
    });
  }

  return out;
};

const transformPickemsEntrySelectionsMap = (
  selectionsMap: PickemsEntry["data"]["selectionsMap"],
) => {
  return Object.values(selectionsMap)
    .sort((a, b) =>
      a.event.scheduledStartTime < b.event.scheduledStartTime ? -1 : 1,
    )
    .map((selection, index) => {
      const {
        competitor,
        event,
        points,
        result,
        outcomeId,
        outcomeName,
        outcomeType,
      } = selection;

      const teamNames = event.eventName?.split(/ vs.? /, 2);
      const parentName = event.attributes?.tournamentName || null;

      return {
        competitor,
        eventId: event.eventId,
        eventName: event.eventName,
        index: `Pick ${index + 1}`,
        isPlaceholder: false,
        odds: points,
        outcomeName: outcomeName ?? competitor?.name,
        outcomeId,
        outcomeType,
        result,
        scheduledStartTime: parseDate(event.scheduledStartTime),
        shortIndex: `${index + 1}`,
        teamNames: teamNames?.length === 2 ? teamNames : undefined,
        sport: event?.sport?.toLowerCase(),
        parentName,
        marketStatus: selection.marketStatus,
        marketId: selection.marketId,
      };
    });
};

export const transformPickemsEntry = (
  entry: PickemsEntry & { contestId: string },
) => {
  const decided = ["SETTLED", "REJECTED", "CANCELLED", "VOID"].includes(
    entry.data.status,
  );
  const status =
    entry.data.status === "LIVE" ? {} : getEntryStatus(entry.data.status);

  const picks = transformPickemsEntrySelectionsMap(entry.data.selectionsMap);

  return {
    contestId: entry.contestId,
    createdAt: parseDate(entry.data.createdAt),
    updatedAt: parseDate(entry.updatedAt),
    currency: entry.data.currency,
    decided,
    entryStatusText: status?.text,
    entryStatusVariant: status?.variant,
    entryType: "pickem",
    id: `P-${entry.data.contestNumber}-${entry.entryId}`,
    name: entry.data.contestTitle ?? "Pick'ems Contest",
    payOut: entry.data.paidOut || 0,
    picks,
    place: entry.data.place,
    points: entry.data.points,
    status: entry.data.status,
  };
};
